/* eslint-disable no-duplicate-imports */
/* eslint-disable prettier/prettier */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import {
  Box,
  Select,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
  FormHelperText
} from '@mui/material';
import { useForm, Controller, FieldValues } from 'react-hook-form';
import * as yup from 'yup';
import { API } from '../../../../api';
import { toast } from 'react-toastify';
import ROLES from '../../../../utils/roles';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '../../../../redux/hooks';
import useOrgSettings from '../../../../hooks/useOrgSettings';
import useLessonVideoBuilder from '../../../../hooks/useLessonVideoBuilder';
import BrandKitCard from '../../../../components/video-builder/BrandKitCard';
import { handleImageCompression, imageSizeLimit } from '../../components/common';
import { IOrgData, orgSettingsActions } from '../../../../redux/slices/orgDetails/orgDetailsSlice';
import { ORG_TYPES } from '../../../../utils/constants';
import useGroupDetails from '../../../../queries/useGroupDetails';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import AudienceAttributesSection from './AudienceAttributesSection';
import { cardHeaderSx, cardSx, cardTitleSx, fieldLabelSx, inputSx, menuPaperSx, selectSx } from './orgSettingsStyles';
import { popupIcon } from './orgSettingsInputs';
import { pickLabel } from '../../../../utils/memberTypeLabels';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

const imgSize = imageSizeLimit();

interface Props {
  data: IOrgData;
}

const CURRENCIES = [
  { value: '$', label: '$ US Dollar' },
  { value: '€', label: '€ Euro' },
  { value: '£', label: '£ Pound Sterling' }
];

// Suggestion lists for the learners/mentors/admins label fields. They read
// capitalised, like the labels everywhere else (pickLabel), and are saved
// lowercase (toStoredLabel), the way the BE has always kept these words.
const MENTEE_OPTIONS = ['Learner', 'Mentee', 'Client', 'Employee', 'Student'];
const MENTOR_OPTIONS = ['Mentor', 'Consultant', 'Coach', 'Student', 'Faculty', 'Manager', 'Buddy', 'Employee'];
const ADMIN_ROLE_OPTIONS = ['Admin', 'Staff'];

// A picked option goes back lowercase; a word the admin typed is kept as typed.
const toStoredLabel = (value: unknown, options: string[]) => {
  const label = String((Array.isArray(value) ? value[0] : value) ?? '').trim();
  return options.some((option) => option.toLowerCase() === label.toLowerCase()) ? label.toLowerCase() : label;
};

// Two cards side by side, wider one first; the cards stretch to the taller
// one's height so the row has no empty corners. One column below lg.
const rowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 3fr) minmax(0, 2fr)' },
  gap: '25px',
  alignItems: 'stretch',
  [MOBILE_SX]: { gap: '16px' }
};

// Each card on the page saves and cancels only its own fields.
type Card = 'info' | 'members';
const CARD_FIELDS: Record<Card, string[]> = {
  info: ['orgName', 'orgType', 'currency', 'image', 'description'],
  members: ['mentee', 'mentor', 'admin']
};
const CARD_TITLES: Record<Card, string> = { info: 'Organization information', members: 'People' };
// What the settings POST refuses empty (OrgSettings' orgSettingsSchema).
const SERVER_REQUIRED = ['orgName', 'orgType', 'currency', 'mentee', 'mentor'];
const blank = (v: unknown) =>
  v === undefined || v === null || (Array.isArray(v) ? v.every((x) => !String(x ?? '').trim()) : !String(v).trim());

const OrganizationInformation = ({ data }: Props) => {
  const [savingCard, setSavingCard] = useState<Card | null>(null);
  // Brand Kit card (video builder) — flag-gated.
  const videoBuilder = useLessonVideoBuilder();
  const [imgError, setImgError] = useState('');
  const dispatch = useAppDispatch();
  const schema = yup.object({
    orgName: yup
      .string()
      .required('Organization name is required')
      .matches(/^[a-zA-Z\s\S]+$/, `Name should contain only letters, spaces, and special characters`)
      .test('no-leading-spaces', 'Name cannot accept empty spaces', (value) => {
        if (value) { return !/^\s+$/.test(value); }
        return true;
      })
      .min(3, 'Minimum 3 characters'),
    orgType: yup.string().required('Organization type is required'),
    currency: yup.mixed().test({ message: 'Pick a currency', test: (value) => !blank(value) }),
    mentee: yup.mixed().nullable().test({
      message: 'This field is required',
      test: () => {
        const menteeValues: any = getValues('mentee');
        const arr = typeof menteeValues === 'string' ? [menteeValues] : menteeValues;
        return Array.isArray(arr) && arr.length > 0;
      }
    }),
    mentor: yup.mixed().nullable().test({
      message: 'This field is required',
      test: () => {
        const mentorValues: any = getValues('mentor');
        const arr = typeof mentorValues === 'string' ? [mentorValues] : mentorValues;
        return Array.isArray(arr) && arr.length > 0;
      }
    }),
    image: yup
      .mixed()
      .test('fileValidation', '', (value, { createError }) => {
        if (!value || !(value instanceof File)) return true;
        if (value.size > imgSize.limitValue) {
          setImgError('');
          return createError({ message: 'The file is too large. Allowed maximum size is ' + imgSize.limitText });
        }
        if (!/\.(jpg|jpeg|png|svg)$/i.test(value.name)) {
          setImgError('');
          return createError({ message: 'Only jpg, png and svg files are allowed' });
        }
        return true;
      })
      .nullable()
  });

  const {
    control,
    setValue,
    getValues,
    trigger,
    clearErrors,
    watch,
    formState: { errors, defaultValues },
    reset
  } = useForm({ resolver: yupResolver(schema) });

  // Watching the form re-renders on every edit, so each card knows at once
  // whether it has changes.
  const watched = watch();
  const savedValue = (f: string) => (defaultValues as FieldValues | undefined)?.[f];
  const cardDirty = (card: Card) => CARD_FIELDS[card].some((f) => !_.isEqual(watched[f], savedValue(f)));
  // The other card's unsaved edits, put back once a save has reloaded the form.
  const keepAfterSaveRef = useRef<FieldValues>({});

  const { location, groupId } = useProgramContext();
  const grpId = data?.grpId || groupId || '';
  const checkError = (fieldName: string) => Boolean(errors[fieldName]);
  const getError = (fieldName: string) => errors[fieldName]?.message;
  const isOrgSettings = useOrgSettings();

  const { refetch: refetchGroupDetails } = useGroupDetails(grpId, location);
  // Save one card: its own fields as edited, every other field as saved (the
  // settings POST takes the whole form). The other card's edits stay on screen.
  // When the other card's saved fields can't pass the server (an org that never
  // named its mentors, say), its fields as edited go too, and must be valid.
  const saveCard = async (card: Card) => {
    if (!(await trigger(CARD_FIELDS[card]))) return;
    const current = getValues();
    const values: FieldValues = { ...current };
    const other: Card = card === 'info' ? 'members' : 'info';
    const otherSavedBlank = CARD_FIELDS[other].some((f) => SERVER_REQUIRED.includes(f) && blank(savedValue(f)));
    if (otherSavedBlank && !(await trigger(CARD_FIELDS[other]))) {
      toast.error(`Fill in the required fields in ${CARD_TITLES[other]} first.`);
      return;
    }
    keepAfterSaveRef.current = {};
    if (!otherSavedBlank) {
      CARD_FIELDS[other].forEach((f) => {
        if (!_.isEqual(current[f], savedValue(f))) keepAfterSaveRef.current[f] = current[f];
        values[f] = savedValue(f);
      });
    }
    setSavingCard(card);
    // Guarantee a non-empty userTypes (no UI for it) — default to Mentee/Mentor
    // when absent so the BE's required min(1) is always satisfied.
    const userTypes =
      Array.isArray(values.userTypes) && values.userTypes.length > 0 ? values.userTypes : ['Mentee', 'Mentor'];
    const trimmedValues: FieldValues = {
      ...values,
      orgName: String(values.orgName ?? '').trim(),
      mentee: toStoredLabel(values.mentee, MENTEE_OPTIONS),
      mentor: toStoredLabel(values.mentor, MENTOR_OPTIONS),
      admin: toStoredLabel(values.admin, ADMIN_ROLE_OPTIONS),
      userTypes
    };
    const formData = new FormData();
    Object.keys(trimmedValues).forEach((key) => {
      if (trimmedValues[key] !== undefined) formData.append(key, trimmedValues[key]);
    });
    try {
      const response = await API.updateOrganizationSettings({ orgId: data.authId, formData });
      if (response.status === 200) {
        dispatch(orgSettingsActions.updateOrgSettings(response.data));
        toast.success('Organization settings updated successfully');
        const role = localStorage.getItem('role');
        if (!isOrgSettings && role !== ROLES.platAdmin) { window.location.replace('/'); }
        if (!isOrgSettings && role === ROLES.platAdmin) { refetchGroupDetails(); }
      }
    } catch (e) {
      keepAfterSaveRef.current = {};
      const message = _.get(e, 'response.data.message');
      toast.error(typeof message === 'string' && message ? message : 'Organization settings could not be saved. Try again.');
    } finally {
      setSavingCard(null);
    }
  };

  // Cancel one card: its fields go back to what is saved.
  const cancelCard = (card: Card) => {
    CARD_FIELDS[card].forEach((f) => setValue(f, savedValue(f), { shouldDirty: true }));
    clearErrors(CARD_FIELDS[card]);
    if (card === 'info') setImgError('');
  };

  const cardButtons = (card: Card) => {
    const dirty = cardDirty(card);
    const saving = savingCard === card;
    return (
      <Box sx={{ display: 'flex', gap: '10px', ml: 'auto' }}>
        {dirty && (
          <DosenButton sizeVariant="sm" tone="basic" disabled={saving} onClick={() => cancelCard(card)}>
            Cancel Changes
          </DosenButton>
        )}
        <DosenButton
          data-testid={card === 'info' ? 'org-info-save' : 'org-members-save'}
          sizeVariant="sm"
          tone="cta"
          loading={saving}
          loadingText="Saving..."
          disabled={!dirty || savingCard !== null}
          onClick={() => saveCard(card)}
        >
          Save Changes
        </DosenButton>
      </Box>
    );
  };

  // Seed every editable field as a react-hook-form default in one reset. This
  // is what makes each card's changes meaningful: its Save Changes stays disabled
  // until the admin actually changes something, and re-runs after a successful
  // save (which pushes fresh `data` into redux) so the button disables itself
  // again. Keeping the defaults here — rather than via post-mount setValue —
  // means the setValue'd fields count as pristine, not as pending changes.
  // Keyed on the fields it seeds, so a change elsewhere in the org (Audience
  // attributes saving its lists) leaves this form's unsaved edits alone.
  const seedKey = JSON.stringify([
    data.name,
    data.description,
    data.orgLogo,
    data.orgType,
    data.displayRoles,
    data.currency,
    data.userTypes
  ]);
  useEffect(() => {
    reset({
      orgName: data.name,
      description: data.description,
      image: data.orgLogo,
      orgType: data.orgType,
      // Shown capitalised; an org that hasn't named them reads Learner / Mentor / Admin.
      mentee: [pickLabel(data?.displayRoles?.mentee, 'Learner')],
      mentor: [pickLabel(data?.displayRoles?.mentor, 'Mentor')],
      admin: [pickLabel(data?.displayRoles?.admin, 'Admin')],
      currency: data?.currency || [],
      // userTypes has no UI — default to Mentee/Mentor when the org has none so a
      // plain save never sends an empty list (which the BE rejects). Existing
      // user types are preserved untouched for orgs that already have them.
      userTypes:
        Array.isArray(data?.userTypes) && data.userTypes.length > 0 ? data.userTypes : ['Mentee', 'Mentor']
    });
    // A save of one card reloads the form; the other card keeps its edits.
    Object.entries(keepAfterSaveRef.current).forEach(([f, v]) => setValue(f, v, { shouldDirty: true }));
    keepAfterSaveRef.current = {};
  }, [seedKey, reset]);

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setImgError('');
    const photo = event.target.files ? event.target.files[0] : null;
    if (photo && photo.size > imgSize.limitValue) {
      setImgError('The file is too large. Allowed maximum size is ' + imgSize.limitText);
    }
    if (photo) {
      const fileType = photo.type.toLowerCase();
      if (fileType === 'image/jpeg' || fileType === 'image/jpg' || fileType === 'image/png') {
        try {
          const compressedImage = await handleImageCompression(photo);
          if (compressedImage) setValue('image', compressedImage, { shouldDirty: true });
        } catch {}
      } else {
        setImgError('Only JPG, JPEG, and PNG files are allowed for the cover image.');
      }
    }
    // Let the same file be picked again (after Cancel Changes, say).
    event.target.value = '';
  };

  return (
    <>
      {/* ── Outer table card ─────────────────────────────────── */}
      <Box
        sx={{
          background: '#1C1C1C',
          overflow: 'hidden',
          borderRadius: '8px',
          outline: '1px solid #2A2A2A',
          outlineOffset: '-1px',
          display: 'flex',
          flexDirection: 'column',
          // A phone: no frame around the cards; each card sits on the page.
          [MOBILE_SX]: { background: 'transparent', outline: 'none', borderRadius: 0, overflow: 'visible' }
        }}
      >
        {/* Body */}
        <Box
          sx={{
            background: '#151515',
            p: '25px',
            display: 'flex',
            flexDirection: 'column',
            gap: '25px',
            [MOBILE_SX]: { background: 'transparent', p: 0, gap: '16px' }
          }}
        >
          {/* ── Row 1: Organization information + People ───── */}
          <Box sx={rowSx}>
          <Box sx={cardSx}>
            <Box sx={cardHeaderSx}>
              <Typography sx={cardTitleSx}>Organization information</Typography>
              {cardButtons('info')}
            </Box>

            <Box
              sx={{
                px: '25px',
                py: '15px',
                display: 'flex',
                gap: '20px',
                alignItems: 'flex-start',
                // A phone: the logo above the fields, each the card's full width.
                [MOBILE_SX]: { flexDirection: 'column', alignItems: 'stretch', px: '16px', gap: '16px' }
              }}
            >
              {/* Logo column */}
              <Box
                sx={{
                  width: 220,
                  [MOBILE_SX]: { width: 'auto' },
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '15px',
                  alignSelf: 'center'
                }}
              >
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => {
                    const hasLogo = field.value && (
                      field.value instanceof File ||
                      (typeof field.value === 'string' && field.value.trim() !== '')
                    );
                    const logoSrc = hasLogo
                      ? (typeof field.value === 'string'
                          ? field.value
                          : URL.createObjectURL(field.value as File))
                      : null;
                    return (
                      <>
                        <label htmlFor="orgLogoInput" style={{ cursor: 'pointer', display: 'inline-block' }}>
                          <Box
                            sx={{
                              width: 100,
                              height: 100,
                              borderRadius: '5px',
                              background: '#444444',
                              outline: '1px solid #868686',
                              outlineOffset: '-1px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden'
                            }}
                          >
                            {logoSrc ? (
                              <img
                                src={logoSrc}
                                alt="Company Logo"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <ImageOutlinedIcon sx={{ color: '#FDFDFD', fontSize: 24 }} />
                            )}
                          </Box>
                          <input
                            id="orgLogoInput"
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleImageChange}
                          />
                        </label>

                        <Box
                          sx={{
                            py: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '15px'
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#FDFDFD',
                              fontSize: 18,
                              fontFamily: 'Inter',
                              fontWeight: 700,
                              lineHeight: '19.8px',
                              textAlign: 'center'
                            }}
                          >
                            Company logo
                          </Typography>
                          <Typography
                            sx={{
                              color: '#868686',
                              fontSize: 12,
                              fontFamily: 'Inter',
                              fontWeight: 500,
                              lineHeight: '13.2px',
                              textAlign: 'center'
                            }}
                          >
                            *PNG or SVG (max. 800x800 px)
                          </Typography>
                        </Box>

                        {imgError && (
                          <FormHelperText error sx={{ textAlign: 'center' }}>
                            {imgError}
                          </FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
              </Box>

              {/* Form fields column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Organization Name */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Typography sx={fieldLabelSx}>Organization Name *</Typography>
                  <Controller
                    control={control}
                    name="orgName"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        fullWidth
                        size="small"
                        onChange={onChange}
                        value={value || ''}
                        placeholder="Type or select your company"
                        error={checkError('orgName')}
                        helperText={getError('orgName')?.toString()}
                        sx={inputSx}
                      />
                    )}
                  />
                </Box>

                {/* Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Typography sx={fieldLabelSx}>Type*</Typography>
                  <Controller
                    name="orgType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <Select
                          value={value || ''}
                          onChange={onChange}
                          displayEmpty
                          disabled={data.orgType === ORG_TYPES.govt}
                          error={checkError('orgType')}
                          sx={{ ...selectSx, color: value ? '#FDFDFD' : '#868686' }}
                          IconComponent={KeyboardArrowDownRoundedIcon}
                          MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                        >
                          <MenuItem value="" sx={{ color: '#868686' }}>Please Select</MenuItem>
                          <MenuItem value={ORG_TYPES.highEd}>{ORG_TYPES.highEd}</MenuItem>
                          <MenuItem value={ORG_TYPES.govt}>{ORG_TYPES.govt}</MenuItem>
                          <MenuItem value={ORG_TYPES.corporate}>{ORG_TYPES.corporate}</MenuItem>
                        </Select>
                        <FormHelperText error>{getError('orgType')?.toString()}</FormHelperText>
                      </>
                    )}
                  />
                </Box>

                {/* Currency */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pb: '10px' }}>
                  <Typography sx={fieldLabelSx}>Currency*</Typography>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: '40px', rowGap: '12px' }}>
                        {CURRENCIES.map((currency) => {
                          const selected = value === currency.value;
                          return (
                            <Box
                              key={currency.value}
                              onClick={() => onChange(currency.value)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                cursor: 'pointer'
                              }}
                            >
                              <Box sx={{ position: 'relative', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    background: 'rgba(0,0,0,0.90)',
                                    borderRadius: '50%',
                                    border: `2px solid ${selected ? '#FDFDFD' : '#868686'}`
                                  }}
                                />
                                {selected && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      width: 10,
                                      height: 10,
                                      background: '#FDFDFD',
                                      borderRadius: '50%'
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 700 }}>
                                {currency.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  />
                  {checkError('currency') && (
                    <FormHelperText error sx={{ mt: '-10px' }}>{getError('currency')?.toString()}</FormHelperText>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

            {/* People card */}
            <Box sx={cardSx}>
              <Box sx={cardHeaderSx}>
                <Typography sx={cardTitleSx}>People</Typography>
                {cardButtons('members')}
              </Box>
              <Box sx={{ px: '25px', py: '15px', display: 'flex', flexDirection: 'column', gap: '20px', [MOBILE_SX]: { px: '16px' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  {/* Learners */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography sx={fieldLabelSx}>Name your learners:</Typography>
                    <Controller
                      control={control}
                      name="mentee"
                      render={({ field: { onChange, value } }) => {
                        const strValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
                        return (
                          <Autocomplete
                            freeSolo
                            options={MENTEE_OPTIONS}
                            value={strValue}
                            inputValue={strValue}
                            onInputChange={(_e, newInput, reason) => {
                              if (reason !== 'reset') onChange(newInput ? [newInput] : []);
                            }}
                            onChange={(_e, newValue) => {
                              if (typeof newValue === 'string') onChange(newValue ? [newValue] : []);
                              else if (newValue === null) onChange([]);
                            }}
                            forcePopupIcon
                            popupIcon={popupIcon}
                            componentsProps={{ paper: { sx: menuPaperSx } }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select"
                                error={checkError('mentee')}
                                sx={inputSx}
                              />
                            )}
                          />
                        );
                      }}
                    />
                    {checkError('mentee') && (
                      <FormHelperText error>{getError('mentee')?.toString()}</FormHelperText>
                    )}
                  </Box>

                  {/* Mentors */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography sx={fieldLabelSx}>
                      Name your mentors{' '}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontFamily: 'Inter',
                          fontStyle: 'italic',
                          fontWeight: 400,
                          lineHeight: '20px'
                        }}
                      >
                        (only used for Mentorship)
                      </Typography>
                      :
                    </Typography>
                    <Controller
                      control={control}
                      name="mentor"
                      render={({ field: { onChange, value } }) => {
                        const strValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
                        return (
                          <Autocomplete
                            freeSolo
                            options={MENTOR_OPTIONS}
                            value={strValue}
                            inputValue={strValue}
                            onInputChange={(_e, newInput, reason) => {
                              if (reason !== 'reset') onChange(newInput ? [newInput] : []);
                            }}
                            onChange={(_e, newValue) => {
                              if (typeof newValue === 'string') onChange(newValue ? [newValue] : []);
                              else if (newValue === null) onChange([]);
                            }}
                            forcePopupIcon
                            popupIcon={popupIcon}
                            componentsProps={{ paper: { sx: menuPaperSx } }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select"
                                error={checkError('mentor')}
                                sx={inputSx}
                              />
                            )}
                          />
                        );
                      }}
                    />
                    {checkError('mentor') && (
                      <FormHelperText error>{getError('mentor')?.toString()}</FormHelperText>
                    )}
                  </Box>

                  {/* Admins — labels displayRoles.admin (admin/staff). */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography sx={fieldLabelSx}>Name your admins:</Typography>
                    <Controller
                      control={control}
                      name="admin"
                      render={({ field: { onChange, value } }) => {
                        const strValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
                        return (
                          <Autocomplete
                            freeSolo
                            options={ADMIN_ROLE_OPTIONS}
                            value={strValue}
                            inputValue={strValue}
                            onInputChange={(_e, newInput, reason) => {
                              if (reason !== 'reset') onChange(newInput ? [newInput] : []);
                            }}
                            onChange={(_e, newValue) => {
                              if (typeof newValue === 'string') onChange(newValue ? [newValue] : []);
                              else if (newValue === null) onChange([]);
                            }}
                            forcePopupIcon
                            popupIcon={popupIcon}
                            componentsProps={{ paper: { sx: menuPaperSx } }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Select" sx={inputSx} />
                            )}
                          />
                        );
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Row 2: Audience Attributes, full width ─────── */}
          <AudienceAttributesSection data={data} grpId={grpId} />

          {/* ── Row 3: Brand Kit (AI lesson videos), full width — least important, so last; flag-gated ── */}
          {videoBuilder && <BrandKitCard />}
        </Box>
      </Box>

    </>
  );
};

export default OrganizationInformation;
